/**
 * useAuth — the console's session, from NU Core SSO (#590).
 *
 * Sign-in is Authorization Code + PKCE through the console API
 * (src/services/sso/ssoSession.ts): `login()` navigates away to NU Core and
 * the `/auth/callback` page calls `completeLogin()`. There is no password, no
 * local credential form and no client-side MFA step — identity, MFA and
 * password policy are NU Core's (the TOTP / email-OTP callbacks that sat here
 * against Lane A's retired methods, #659, are gone).
 *
 * Storage: the access token and the principal the API returned live in the
 * tokenStore seam (sessionStorage, #559/#598/#601). `user.role` / `user.roles`
 * are NU Core's canonical roles (ROLE-MATRIX §1) — the vocabulary the route
 * guards and nav read.
 *
 * Expiry: checked every 15 s; an expired session is cleared and the browser
 * goes to `/login?auto=1`, which re-runs the PKCE round-trip silently while
 * the user's NU Core session is alive.
 */
import { useCallback, useEffect, useState } from "react";
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  getAuthValue,
} from "../services/tokenStore";
import {
  completeLogin as completeSsoLogin,
  callbackErrorMessage,
  clearSession,
  endSession,
  refreshSessionUser,
  startLogin,
  type SessionUser,
} from "../services/sso/ssoSession";
import { navigateTo } from "../utils/navigation";
import type { CanonicalRole } from "../rbac/roles";

export type UserRole = CanonicalRole;

export interface User {
  id: string;
  /** Display name (falls back to the email). */
  username: string;
  email: string;
  role: CanonicalRole;
  roles: CanonicalRole[];
  /** Local NU DATA tenant id. */
  tenantId: string;
  mfaVerified: boolean;
  token: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

export interface LoginResult {
  success?: boolean;
  error?: string;
  /** Where to go after a successful callback. */
  returnTo?: string;
  /** The API's refusal code when the callback failed (e.g. tenant_unmapped). */
  code?: string | null;
}

const ADMIN_TIER: ReadonlySet<CanonicalRole> = new Set([
  "owner",
  "admin",
  "tenant_admin",
]);

function tokenExpired(token: string): boolean {
  try {
    const part = token.split(".")[1] ?? "";
    const payload = JSON.parse(
      atob(part.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: unknown };
    if (typeof payload.exp !== "number") return true;
    return Date.now() / 1000 > payload.exp - 60;
  } catch {
    return true;
  }
}

function toUser(session: SessionUser, token: string): User {
  return {
    id: session.id,
    username: session.name || session.email,
    email: session.email,
    role: session.role,
    roles: session.roles?.length ? session.roles : [session.role],
    tenantId: session.tenantId,
    mfaVerified: session.mfaVerified === true,
    token,
  };
}

/** The stored session, or null (clearing anything expired or corrupt). */
function readStoredUser(): User | null {
  const token = getAuthValue(AUTH_TOKEN_KEY);
  const raw = getAuthValue(AUTH_USER_KEY);
  if (!token || !raw) return null;
  if (tokenExpired(token)) {
    clearSession();
    return null;
  }
  try {
    const session = JSON.parse(raw) as SessionUser;
    if (!session?.id || !session.role) throw new Error("incomplete");
    return toUser(session, token);
  } catch {
    clearSession();
    return null;
  }
}

function currentPath(): string {
  if (typeof window === "undefined" || !window.location) return "/";
  return `${window.location.pathname}${window.location.search}`;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
  });

  // Restore, then re-validate against the API (role, tenant, deactivation).
  useEffect(() => {
    let cancelled = false;
    const stored = readStoredUser();
    setAuthState({ user: stored, isLoading: false, error: null });
    if (stored) {
      void refreshSessionUser().then((fresh) => {
        if (cancelled) return;
        const token = getAuthValue(AUTH_TOKEN_KEY);
        setAuthState((prev) => ({
          ...prev,
          user:
            fresh && token ? toUser(fresh, token) : token ? prev.user : null,
        }));
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Expiry: clear and re-run the (silent) SSO round-trip.
  useEffect(() => {
    const interval = setInterval(() => {
      const token = getAuthValue(AUTH_TOKEN_KEY);
      if (token && tokenExpired(token)) {
        clearSession();
        setAuthState({
          user: null,
          isLoading: false,
          error: "Session expired. Signing you in again…",
        });
        navigateTo(
          `/login?auto=1&returnTo=${encodeURIComponent(currentPath())}`,
        );
      }
    }, 15_000);
    return () => clearInterval(interval);
  }, []);

  /** Start NU Core sign-in. Navigates away; resolves only if it cannot. */
  const login = useCallback(
    async (options: { returnTo?: string } = {}): Promise<LoginResult> => {
      setAuthState((prev) => ({ ...prev, error: null }));
      startLogin(options.returnTo ?? "/");
      return { success: true };
    },
    [],
  );

  /** Finish sign-in on /auth/callback. */
  const completeLogin = useCallback(
    async (code: string, state: string): Promise<LoginResult> => {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const { returnTo } = await completeSsoLogin(code, state);
        const user = readStoredUser();
        if (!user) {
          // The API answered but no usable session could be read back
          // (expired/corrupt token, storage unavailable): not a sign-in (#972).
          const failed = new Error(
            callbackErrorMessage("token_exchange_failed"),
          ) as Error & { code?: string };
          failed.code = "token_exchange_failed";
          throw failed;
        }
        setAuthState({ user, isLoading: false, error: null });
        return { success: true, returnTo };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Sign-in failed";
        setAuthState({ user: null, isLoading: false, error: msg });
        return {
          error: msg,
          code: (err as { code?: string | null } | null)?.code ?? null,
        };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    endSession();
    setAuthState({ user: null, isLoading: false, error: null });
  }, []);

  const clearError = useCallback(() => {
    setAuthState((prev) => ({ ...prev, error: null }));
  }, []);

  const { user, isLoading, error } = authState;

  return {
    user,
    isLoading,
    error,
    isAuthenticated: user !== null,
    /** Admin-tier canonical role (owner/admin/tenant_admin) — UI only. */
    isAdmin: !!user && user.roles.some((r) => ADMIN_TIER.has(r)),
    login,
    completeLogin,
    logout,
    clearError,
  };
}
